import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ETFResultsPanel.module.css';
import TVChart from './TVChart';
import PriceTicker from './PriceTicker';
import ZakatCalculator from './ZakatCalculator';

function fmt(n) {
  if (!n && n !== 0) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e12) return '$' + (n / 1e12).toFixed(2) + 'T';
  if (abs >= 1e9)  return '$' + (n / 1e9).toFixed(2)  + 'B';
  if (abs >= 1e6)  return '$' + (n / 1e6).toFixed(2)  + 'M';
  return '$' + n.toLocaleString();
}

const STANDARDS = ['AAOIFI', 'DJIM', 'MSCI'];

function WeightBar({ s, screenedWeight }) {
  const { t } = useTranslation();
  const portfolioGap = Math.max(0, +(100 - screenedWeight).toFixed(1));
  const totalUnscreened = +((s.unknownWeight || 0) + portfolioGap).toFixed(1);

  return (
    <>
      <div className={styles.weightBar}>
        {s.compliantWeight    > 0 && <div className={styles.weightSeg} style={{ width: s.compliantWeight    + '%', background: 'var(--green)'   }} />}
        {s.conditionalWeight  > 0 && <div className={styles.weightSeg} style={{ width: s.conditionalWeight  + '%', background: 'var(--amber)'   }} />}
        {s.nonCompliantWeight > 0 && <div className={styles.weightSeg} style={{ width: s.nonCompliantWeight + '%', background: 'var(--red)'     }} />}
        {totalUnscreened      > 0 && <div className={styles.weightSeg} style={{ width: totalUnscreened      + '%', background: 'var(--border2)' }} />}
      </div>
      <div className={styles.weightLegend}>
        {s.compliantWeight    > 0 && <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: 'var(--green)'   }} />{s.compliantWeight}% {t('etf_results.legend.compliant')}</span>}
        {s.conditionalWeight  > 0 && <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: 'var(--amber)'   }} />{s.conditionalWeight}% {t('etf_results.legend.conditional')}</span>}
        {s.nonCompliantWeight > 0 && <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: 'var(--red)'     }} />{s.nonCompliantWeight}% {t('etf_results.legend.non_compliant')}</span>}
        <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: 'var(--border2)', border: '1px solid var(--border2)' }} />{totalUnscreened}% {t('etf_results.legend.unscreened')}</span>
      </div>
    </>
  );
}

export default function ETFResultsPanel({ data, theme, onRescreen, onScreen, isWatchlisted, onWatchlistToggle }) {
  const { t } = useTranslation();
  const { etf, holdings, summary, standards, holdingsUnavailable } = data;
  const [activeStd, setActiveStd] = useState('AAOIFI');
  const [tab,       setTab]       = useState('Holdings'); // 'Holdings' | 'Zakat'

  const sorted = [...holdings].sort((a, b) => (b.weight || 0) - (a.weight || 0));
  const maxWeight = Math.max(...holdings.map(h => h.weight || 0), 0.01);

  const activeStdData = standards?.[activeStd.toLowerCase()];

  const holdingVerdict = (h) => {
    if (activeStd === 'DJIM') return h.djimVerdict;
    if (activeStd === 'MSCI') return h.msciVerdict;
    return h.status; // AAOIFI
  };

  const pillClass = (v) => ({
    COMPLIANT:     styles.pillPass,
    CONDITIONAL:   styles.pillWarn,
    NON_COMPLIANT: styles.pillFail,
    UNKNOWN:       styles.pillUnknown,
  }[v] || styles.pillUnknown);

  const pillLabel = (v) => t(`etf_results.pill.${v}`) || '?';

  return (
    <div className={styles.panel}>

      {/* ── Row 1: ticker (50%) | standards (50%) ── */}
      <div className={styles.headerRow}>
        <div className={styles.headerLeft}>
          <h2 className={styles.ticker}>
            {etf.ticker}
            <span className={styles.etfBadge}>{t('etf_results.etf_badge')}</span>
          </h2>
          <p className={styles.company}>{etf.companyName}</p>
          <p className={styles.meta}>
            {etf.exchange}
            {etf.stockPosition > 0 && ` · ${(etf.stockPosition * 100).toFixed(0)}% Equities`}
            {etf.bondPosition  > 0 && ` · ${(etf.bondPosition  * 100).toFixed(0)}% Bonds`}
            {etf.totalAssets   > 0 && ` · AUM ${fmt(etf.totalAssets)}`}
          </p>
          <p className={styles.source}>
            {t('etf_results.screened_holdings', { n: etf.holdingsShown, pct: +(100 - summary.screenedWeight).toFixed(1) })}
          </p>
        </div>
        <div className={styles.headerRight}>
          {onWatchlistToggle && (
            <button
              className={`${styles.watchlistBtn} ${isWatchlisted ? styles.watchlistBtnActive : ''}`}
              onClick={() => onWatchlistToggle(etf.ticker, etf.companyName, summary.overallVerdict, etf.price)}
              aria-label={isWatchlisted ? t('etf_results.watchlist.remove_aria') : t('etf_results.watchlist.save')}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill={isWatchlisted ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
              {isWatchlisted ? t('etf_results.watchlist.saved') : t('etf_results.watchlist.save')}
            </button>
          )}
          {standards && (() => {
            return (
              <>
                <div className={styles.aggregateWrap}>
                  <div className={styles.agreeLine}>
                    {[standards.aaoifi.verdict, standards.djim.verdict, standards.msci.verdict].map((v, i) => (
                      <div key={i} className={styles.agreeSeg} style={{
                        flex: 1,
                        background: v === 'COMPLIANT' ? 'var(--green)' : v === 'CONDITIONAL' ? 'var(--amber)' : v === 'INSUFFICIENT_DATA' ? 'var(--border2)' : 'var(--red)'
                      }} />
                    ))}
                  </div>
                </div>
                <div className={styles.standardsGrid}>
                  {[
                    { name: 'AAOIFI', verdict: standards.aaoifi.verdict, s: standards.aaoifi },
                    { name: 'DJIM',   verdict: standards.djim.verdict,   s: standards.djim },
                    { name: 'MSCI',   verdict: standards.msci.verdict,   s: standards.msci },
                  ].map(({ name, verdict, s }) => (
                    <div key={name} className={styles.standardItem}>
                      <span className={styles.standardName}>{name}</span>
                      <div className={`${styles.standardBadge} ${
                        verdict === 'COMPLIANT'        ? styles.verdictCompliant :
                        verdict === 'CONDITIONAL'      ? styles.verdictConditional :
                        verdict === 'INSUFFICIENT_DATA'? styles.verdictInsufficient :
                                                         styles.verdictFail
                      }`}>
                        {t(`etf_results.verdict.${verdict}`) || verdict}
                      </div>
                      <div className={styles.stdWeights}>
                        {s.compliantWeight    > 0 && <span className={styles.stdWeightLine} style={{ color: 'var(--green)' }}>{s.compliantWeight}% {t('etf_results.legend.compliant')}</span>}
                        {s.conditionalWeight  > 0 && <span className={styles.stdWeightLine} style={{ color: 'var(--amber)' }}>{s.conditionalWeight}% {t('etf_results.legend.conditional')}</span>}
                        {s.nonCompliantWeight > 0 && <span className={styles.stdWeightLine} style={{ color: 'var(--red)'   }}>{s.nonCompliantWeight}% {t('etf_results.legend.non_compliant')}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* ── Row 2: price (25%) | chart (75%) ── */}
      <div className={styles.priceChartRow}>
        <PriceTicker financials={etf} />
        <TVChart symbol={etf.ticker} theme={theme} />
      </div>

      {/* ── Tab navigation ── */}
      <div className={styles.tabNav}>
        {['Holdings', 'Zakat'].map(tabName => (
          <button
            key={tabName}
            className={`${styles.tabBtn} ${tab === tabName ? styles.tabBtnActive : ''}`}
            onClick={() => setTab(tabName)}
          >{tabName}</button>
        ))}
      </div>

      {tab === 'Zakat' && (
        <ZakatCalculator financials={etf} isETF={true} />
      )}

      {tab === 'Holdings' && <>

      {/* ── Standard switcher + per-standard compliance bar ── */}
      <div className={styles.complianceSection}>
        <div className={styles.stdSwitcher}>
          {STANDARDS.map(s => (
            <button
              key={s}
              className={`${styles.stdBtn} ${activeStd === s ? styles.stdBtnActive : ''}`}
              onClick={() => setActiveStd(s)}
            >{s}</button>
          ))}
        </div>
        <p className={styles.complianceLabel}>{t('etf_results.compliance_breakdown')}</p>
        {activeStdData && <WeightBar s={activeStdData} screenedWeight={summary.screenedWeight} />}
      </div>

      {/* ── Holdings table ── */}
      <p className={styles.holdingsLabel}>{t('etf_results.holdings_title', { n: sorted.length, std: activeStd })}</p>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('etf_results.table.symbol')}</th>
              <th>{t('etf_results.table.name')}</th>
              <th>{t('etf_results.table.weight')}</th>
              <th>{t('etf_results.table.verdict')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((h, i) => {
              const v = holdingVerdict(h);
              const weightPct = maxWeight > 0 ? ((h.weight || 0) / maxWeight) * 100 : 0;
              const canScreen = h.symbol && h.symbol !== '—';
              return (
                <tr
                  key={i}
                  className={canScreen ? styles.rowClickable : ''}
                  onClick={canScreen && onScreen ? () => onScreen(h.symbol) : undefined}
                  title={canScreen ? t('etf_results.screen_title', { symbol: h.symbol }) : undefined}
                >
                  <td className={styles.symbolCell}>{h.symbol}</td>
                  <td className={styles.nameCell}>{h.name || '—'}</td>
                  <td className={styles.weightCell}>
                    <div className={styles.weightMini}>
                      <div className={styles.weightMiniFill} style={{ width: weightPct + '%' }} />
                    </div>
                    {h.weight ? (h.weight * 100).toFixed(2) + '%' : '—'}
                  </td>
                  <td className={styles.statusCell}>
                    <span className={`${styles.pill} ${pillClass(v)}`}>{pillLabel(v)}</span>
                    {activeStd === 'AAOIFI' && h.failedCriteria && (
                      <div className={styles.failNote}>{h.failedCriteria}</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Disclaimer ── */}
      <div className={styles.disclaimer}>
        {holdingsUnavailable
          ? <span dangerouslySetInnerHTML={{ __html: t('etf_results.disclaimer_unavailable') }} />
          : <span dangerouslySetInnerHTML={{ __html: t('etf_results.disclaimer', {
              n:     etf.holdingsShown,
              pct:   summary.screenedWeight,
              bonds: etf.bondPosition > 0 ? (etf.bondPosition * 100).toFixed(0) + '%' : '0%',
            }) }} />
        }
      </div>

      </>}

    </div>
  );
}
